import {
  AnalyticsOutlined,
  AssessmentOutlined,
  AssignmentOutlined,
  AudioFileOutlined,
  AutoAwesomeOutlined,
  BusinessOutlined,
  CorporateFareOutlined,
  DashboardOutlined,
  DescriptionOutlined,
  EventNoteOutlined,
  FactCheckOutlined,
  FolderOutlined,
  FolderSpecialOutlined,
  Image,
  ImageOutlined,
  IntegrationInstructionsOutlined,
  ListAltOutlined,
  PagesOutlined,
  PeopleOutline,
  PeopleOutlineOutlined,
  PlayArrowOutlined,
  PlayCircleOutline,
  PlayCircleOutlineOutlined,
  PlaylistPlayOutlined,
  SmartToyOutlined,
  TaskAltOutlined,
  VideocamOutlined,
} from "@mui/icons-material";
import { font } from "../../styles/font";
import { TiDocumentText } from "react-icons/ti";
import { LuLink, LuUnlink } from "react-icons/lu";
import { blue, green, red } from "@mui/material/colors";
import WebhookOutlinedIcon from "@mui/icons-material/WebhookOutlined";
import { FaRegFileExcel, FaRegFilePdf, FaRegFileWord } from "react-icons/fa";
import { COLORS } from "../../styles/theme";
import { BiTask } from "react-icons/bi";
import { memo } from "react";
import { Box, useTheme, Typography } from "@mui/material";
import AIButton from "./AIButton";
import AIEnergyIcon from "./AIEnergyIcon";

const defaultConfig = {
  fontSize: font.size.fs_4,
  color: "inherit",
};

// Helper function to get theme-aware icon color
const getIconColor = (props, theme, defaultColor = "inherit") => {
  // If color is explicitly provided, use it
  if (props?.sx?.color) return props.sx.color;
  if (props?.color) return props.color;
  
  // Otherwise use theme's icon color
  return theme.palette.icon?.main || theme.palette.text.secondary || defaultColor;
};

// Theme-aware icon wrapper component
const ThemeIcon = ({ children, ...props }) => {
  const theme = useTheme();
  
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: getIconColor(props, theme),
        fontSize: props?.sx?.fontSize || defaultConfig.fontSize,
        ...props.sx,
      }}
    >
      {children}
    </Box>
  );
};

export const ProjectCustomIcon = (props) => {
  const theme = useTheme();
  return (
    <ThemeIcon {...props}>
      <FolderSpecialOutlined
        sx={{
          fontSize: props?.sx?.fontSize || defaultConfig.fontSize,
          color: 'inherit',
        }}
      />
    </ThemeIcon>
  );
};

export const ProgramCustomIcon = (props) => {
  const theme = useTheme();
  return (
    <ThemeIcon {...props}>
      <PlaylistPlayOutlined
        sx={{
          fontSize: props?.sx?.fontSize || defaultConfig.fontSize,
          color: 'inherit',
        }}
      />
    </ThemeIcon>
  );
};

export const OrganisationCustomIcon = (props) => {
  const theme = useTheme();
  return (
    <ThemeIcon {...props}>
      <BusinessOutlined
        sx={{
          fontSize: props?.sx?.fontSize || defaultConfig.fontSize,
          color: 'inherit',
        }}
      />
    </ThemeIcon>
  );
};

export const FolderCustomIcon = (props) => {
  const theme = useTheme();
  return (
    <ThemeIcon {...props}>
      <FolderOutlined
        sx={{
          fontSize: props?.sx?.fontSize || defaultConfig.fontSize,
          color: 'inherit',
        }}
      />
    </ThemeIcon>
  );
};

export function SubFolderCustomIcon({
  width,
  height,
  color,
  fill,
  style,
  ...props
}) {
  const theme = useTheme();
  const iconColor = color || theme.palette.icon?.main || theme.palette.text.secondary;
  
  return (
    <ThemeIcon {...props}>
      <svg
        fill={iconColor}
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        height={height || "1em"}
        width={width || "1em"}
        style={{
          fontSize: props?.style?.fontSize || defaultConfig.fontSize,
          color: 'inherit',
          ...style,
        }}
      >
        <g
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
        >
          <path d="M20 10a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1h-2.5a1 1 0 0 1-.8-.4l-.9-1.2A1 1 0 0 0 15 3h-2a1 1 0 0 0-1 1v5a1 1 0 0 0 1 1Zm0 11a1 1 0 0 0 1-1v-3a1 1 0 0 0-1-1h-2.9a1 1 0 0 1-.88-.55l-.42-.85a1 1 0 0 0-.92-.6H13a1 1 0 0 0-1 1v5a1 1 0 0 0 1 1ZM3 5a2 2 0 0 0 2 2h3"></path>
          <path d="M3 3v13a2 2 0 0 0 2 2h3"></path>
        </g>
      </svg>
    </ThemeIcon>
  );
}

export const PageCustomIcon = (props) => {
  const theme = useTheme();
  return (
    <ThemeIcon {...props}>
      <TiDocumentText
        style={{
          fontSize: props?.sx?.fontSize || defaultConfig.fontSize,
          color: 'inherit',
        }}
      />
    </ThemeIcon>
  );
};

export const TasksCustomIcon = (props) => {
  const theme = useTheme();
  return (
    <ThemeIcon {...props}>
      <TaskAltOutlined
        sx={{
          fontSize: props?.sx?.fontSize || defaultConfig.fontSize,
          color: 'inherit',
        }}
      />
    </ThemeIcon>
  );
};

export const NewRegressionCustomIcon = (props) => {
  const theme = useTheme();
  return (
    <ThemeIcon {...props}>
      <WebhookOutlinedIcon
        sx={{
          fontSize: props?.sx?.fontSize || defaultConfig.fontSize,
          color: 'inherit',
        }}
      />
    </ThemeIcon>
  );
};

export const UserStoryCustomIcon = (props) => {
  const theme = useTheme();
  return (
    <ThemeIcon {...props}>
      <DescriptionOutlined
        sx={{
          fontSize: props?.sx?.fontSize || defaultConfig.fontSize,
          color: 'inherit',
        }}
      />
    </ThemeIcon>
  );
};

export const UseCaseCustomIcon = (props) => {
  const theme = useTheme();
  return (
    <ThemeIcon {...props}>
      <IntegrationInstructionsOutlined
        sx={{
          fontSize: props?.sx?.fontSize || defaultConfig.fontSize,
          color: 'inherit',
        }}
      />
    </ThemeIcon>
  );
};

export const TestCaseCustomIcon = (props) => {
  const theme = useTheme();
  return (
    <ThemeIcon {...props}>
      <FactCheckOutlined
        sx={{
          fontSize: props?.sx?.fontSize || defaultConfig.fontSize,
          color: 'inherit',
        }}
      />
    </ThemeIcon>
  );
};

export const LinkCustomIcon = (props) => {
  const theme = useTheme();
  const iconColor = getIconColor(props, theme, theme.palette.success?.main || green[500]);
  
  return (
    <ThemeIcon {...props}>
      <LuLink
        style={{
          fontSize: props?.sx?.fontSize || font.size.fs_3,
          color: 'inherit',
        }}
      />
    </ThemeIcon>
  );
};

export const UnLinkCustomIcon = (props) => {
  const theme = useTheme();
  const iconColor = getIconColor(props, theme, theme.palette.error?.main || red[500]);
  
  return (
    <ThemeIcon {...props}>
      <LuUnlink
        style={{
          fontSize: props?.sx?.fontSize || font.size.fs_3,
          color: 'inherit',
        }}
      />
    </ThemeIcon>
  );
};

export const PdfCustomIcon = (props) => {
  const theme = useTheme();
  const iconColor = getIconColor(props, theme, theme.palette.error?.main || red[500]);
  
  return (
    <ThemeIcon {...props}>
      <FaRegFilePdf
        style={{
          fontSize: props?.sx?.fontSize || font.size.fs_3,
          color: 'inherit',
        }}
      />
    </ThemeIcon>
  );
};

export const ExcelCustomIcon = (props) => {
  const theme = useTheme();
  const iconColor = getIconColor(props, theme, theme.palette.success?.main || green[500]);
  
  return (
    <ThemeIcon {...props}>
      <FaRegFileExcel
        style={{
          fontSize: props?.sx?.fontSize || font.size.fs_3,
          color: 'inherit',
        }}
      />
    </ThemeIcon>
  );
};

export const WordCustomIcon = (props) => {
  const theme = useTheme();
  const iconColor = getIconColor(props, theme, theme.palette.info?.main || blue[500]);
  
  return (
    <ThemeIcon {...props}>
      <FaRegFileWord
        style={{
          fontSize: props?.sx?.fontSize || font.size.fs_3,
          color: 'inherit',
        }}
      />
    </ThemeIcon>
  );
};

export const ImageCustomIcon = (props) => {
  const theme = useTheme();
  return (
    <ThemeIcon {...props}>
      <ImageOutlined
        sx={{
          fontSize: props?.sx?.fontSize || defaultConfig.fontSize,
          color: 'inherit',
        }}
      />
    </ThemeIcon>
  );
};

export const VideoCustomIcon = (props) => {
  const theme = useTheme();
  return (
    <ThemeIcon {...props}>
      <VideocamOutlined
        sx={{
          fontSize: props?.sx?.fontSize || defaultConfig.fontSize,
          color: 'inherit',
        }}
      />
    </ThemeIcon>
  );
};

export const AudioCustomIcon = (props) => {
  const theme = useTheme();
  return (
    <ThemeIcon {...props}>
      <AudioFileOutlined
        sx={{
          fontSize: props?.sx?.fontSize || defaultConfig.fontSize,
          color: 'inherit',
        }}
      />
    </ThemeIcon>
  );
};

export const DashboardCustomIcon = (props) => {
  const theme = useTheme();
  return (
    <ThemeIcon {...props}>
      <AnalyticsOutlined
        sx={{
          fontSize: props?.sx?.fontSize || defaultConfig.fontSize,
          color: 'inherit',
        }}
      />
    </ThemeIcon>
  );
};

export const GeneratedByAICustomIcon = (props) => {
  const theme = useTheme();
  const aiColor = theme.palette.mode === 'dark' 
    ? theme.palette.ai?.light || '#a78bfa' 
    : theme.palette.ai?.main || '#8b5cf6';
  
  return (
    <ThemeIcon {...props} sx={{ color: aiColor, ...props.sx }}>
      <SmartToyOutlined
        sx={{
          fontSize: props?.sx?.fontSize || defaultConfig.fontSize,
          color: 'inherit',
        }}
      />
    </ThemeIcon>
  );
};

export const GeneratedByUserCustomIcon = (props) => {
  const theme = useTheme();
  const userColor = theme.palette.mode === 'dark'
    ? theme.palette.secondary?.light || '#93c5fd'
    : theme.palette.secondary?.main || '#1A73E8';
  
  return (
    <ThemeIcon {...props} sx={{ color: userColor, ...props.sx }}>
      <PeopleOutline
        sx={{
          fontSize: props?.sx?.fontSize || defaultConfig.fontSize,
          color: 'inherit',
        }}
      />
    </ThemeIcon>
  );
};

export const TaskCustomIcon = memo((props) => {
  const theme = useTheme();
  return (
    <ThemeIcon {...props}>
      <BiTask
        style={{
          fontSize: props?.sx?.fontSize || defaultConfig.fontSize,
          color: 'inherit',
        }}
      />
    </ThemeIcon>
  );
});

export function SprintCustomIcon({
  width,
  height,
  color,
  fill,
  style,
  ...props
}) {
  const theme = useTheme();
  const iconColor = color || theme.palette.icon?.main || theme.palette.text.secondary;
  
  return (
    <ThemeIcon {...props}>
      <svg
        fill={iconColor}
        viewBox="0 0 14 14"
        role="img"
        focusable="false"
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        height={height || "1em"}
        width={width || "1em"}
        style={{
          fontSize: props?.style?.fontSize || defaultConfig.fontSize,
          color: 'inherit',
          ...style,
        }}
      >
        <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
        <g
          id="SVGRepo_tracerCarrier"
          strokeLinecap="round"
          strokeLinejoin="round"
        ></g>
        <g id="SVGRepo_iconCarrier">
          <path d="m 9.4619768,9.6829638 c 0.0339,0.0021 0.07045,0.0064 0.107004,0.0064 0.5175422,5.29e-4 1.0356132,-0.0026 1.5531552,0.0026 0.113361,0.0011 0.147263,-0.03761 0.141436,-0.146204 -0.0074,-0.141436 -0.0016,-0.283403 -0.0016,-0.458212 0.587466,0.432256 1.155331,0.849679 1.738029,1.2787572 -0.57846,0.425899 -1.146326,0.843852 -1.738029,1.279816 0,-0.164745 0,-0.300884 0,-0.437023 0,-0.175869 0,-0.176399 -0.171101,-0.176399 -3.3091942,0 -6.6189177,0 -9.9281116,0 -0.159977,0 -0.159977,0 -0.159977,-0.161036 0,-0.349089 0.00477,-0.698178 -0.00265,-1.0467382 -0.00265,-0.113361 0.033902,-0.138788 0.1424961,-0.138258 1.7793473,0.0032 3.5586946,0.0058 5.3380423,0.0011 0.912187,-0.0026 1.632083,-0.380343 2.108306,-1.166455 0.859215,-1.419664 -0.03655,-3.279 -1.682937,-3.518436 -1.2713408,-0.184874 -2.4531579,0.668513 -2.6687561,1.961573 -0.022778,0.13455 -0.024367,0.272808 -0.03761,0.434904 0.208182,-0.02807 0.3967642,-0.05403 0.6102435,-0.08264 -0.3342566,0.646265 -0.6568593,1.270281 -0.9842296,1.902773 -0.5016498,-0.517011 -0.9958835,-1.026077 -1.478993,-1.524019 0.1213071,-0.02013 0.2823436,-0.04344 0.4417909,-0.07681 0.030194,-0.0064 0.074691,-0.05509 0.073632,-0.08317 -0.00901,-0.338495 0.014303,-0.673281 0.080518,-1.005419 0.00212,-0.01007 -0.00265,-0.02172 -0.00583,-0.04132 C 2.6136731,6.4336578 2.3138486,6.3171168 2.0420995,6.1295938 1.377824,5.6697918 1.0303242,4.8142858 1.1934796,4.0345298 c 0.1774579,-0.849679 0.8184255,-1.48482 1.6469159,-1.641618 0.5371415,-0.101178 1.0350832,-0.0048 1.4975334,0.279165 0.081578,0.05032 0.1313719,0.05774 0.1848741,-0.02966 0.031254,-0.05138 0.078399,-0.0927 0.1324314,-0.155209 0.067275,0.271219 0.1303124,0.524957 0.19282,0.778696 0.021189,0.08529 0.03761,0.172161 0.062508,0.256387 0.027016,0.09111 -5.297e-4,0.131902 -0.09694,0.125545 -0.2786355,-0.01907 -0.5578007,-0.03602 -0.8364362,-0.05456 -0.068864,-0.0048 -0.1371989,-0.01218 -0.225663,-0.02066 0.068864,-0.08687 0.1313719,-0.164745 0.1997064,-0.25003 -0.1504272,-0.114454 -0.3162313,-0.171664 -0.4889217,-0.19974 -0.8348471,-0.136669 -1.5706355,0.504828 -1.5536843,1.34974 0.012713,0.648384 0.5593899,1.227903 1.2051251,1.282995 0.083697,0.0069 0.1128314,-0.03549 0.1451447,-0.098 0.4576826,-0.878285 1.1468552,-1.499123 2.084469,-1.822255 2.1692254,-0.747973 4.4338004,0.592232 4.8517546,2.785825 0.206063,1.08011 -0.02861,2.066458 -0.6838762,2.95057 -0.01801,0.02437 -0.03443,0.05032 -0.05032,0.07628 -0.0032,0.0048 0.0011,0.0143 0.0037,0.03443 z"></path>
        </g>
      </svg>
    </ThemeIcon>
  );
}

export function SprintCustomIcon2({ width, height, color, ...props }) {
  const theme = useTheme();
  const iconColor = color || theme.palette.secondary?.main || theme.palette.text.secondary;
  
  return (
    <ThemeIcon {...props}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 2048 2048"
        width={width || "1em"}
        height={height || "1em"}
        fill="currentColor"
      >
        <path d="m2042 1728l-301 301l-90-90l147-147h-383q83-56 152-128h231l-147-147l90-90zm-378-704q0 106-27 204t-78 183t-120 156t-155 120t-184 77t-204 28H0v-128h896q88 0 170-23t153-64t129-100t100-130t65-153t23-170t-23-170t-64-153t-100-129t-130-100t-153-65t-170-23h-14q-7 0-15 2h-6l144 145l-90 90l-301-301L915 19l90 90l-148 148q10-1 19-1t20 0q106 0 204 27t183 78t156 120t120 155t77 184t28 204M325 1536q-95-106-146-237t-51-275q0-94 22-184t66-172t104-151t140-124l93 93q-69 44-124 103t-93 128t-59 147t-21 160q0 76 18 148t51 138t82 123t109 103z" />
      </svg>
    </ThemeIcon>
  );
}

export const PlanningCustomIcon = (props) => {
  const theme = useTheme();
  return (
    <ThemeIcon {...props}>
      <EventNoteOutlined
        sx={{
          fontSize: props?.sx?.fontSize || defaultConfig.fontSize,
          color: 'inherit',
        }}
      />
    </ThemeIcon>
  );
};

export const ExecutionCustomIcon = (props) => {
  const theme = useTheme();
  return (
    <ThemeIcon {...props}>
      <PlayCircleOutlineOutlined
        sx={{
          fontSize: props?.sx?.fontSize || defaultConfig.fontSize,
          color: 'inherit',
        }}
      />
    </ThemeIcon>
  );
};

export const RegressionCustomIcon = (props) => {
  const theme = useTheme();
  return (
    <ThemeIcon {...props}>
      <ListAltOutlined
        sx={{
          fontSize: props?.sx?.fontSize || defaultConfig.fontSize,
          color: 'inherit',
        }}
      />
    </ThemeIcon>
  );
};

export function ReleaseCustomIcon({ width, height, color, style, ...props }) {
  const theme = useTheme();
  const iconColor = color || theme.palette.icon?.main || theme.palette.text.secondary;
  
  return (
    <ThemeIcon {...props}>
      <svg
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        height={height || "1em"}
        width={width || "1em"}
        style={{
          fontSize: props?.style?.fontSize || defaultConfig.fontSize,
          color: 'inherit',
          ...style,
        }}
      >
        <path d="m23 12l-2.44-2.78l.34-3.68l-3.61-.82l-1.89-3.18L12 3L8.6 1.54L6.71 4.72l-3.61.81l.34 3.68L1 12l2.44 2.78l-.34 3.69l3.61.82l1.89 3.18L12 21l3.4 1.46l1.89-3.18l3.61-.82l-.34-3.68zm-4.51 2.11l.26 2.79l-2.74.62l-1.43 2.41L12 18.82l-2.58 1.11l-1.43-2.41l-2.74-.62l.26-2.8L3.66 12l1.85-2.12l-.26-2.78l2.74-.61l1.43-2.41L12 5.18l2.58-1.11l1.43 2.41l2.74.62l-.26 2.79L20.34 12zM11 15h2v2h-2zm0-8h2v6h-2z" />
      </svg>
    </ThemeIcon>
  );
}

export const ReportCustomIcon = (props) => {
  const theme = useTheme();
  return (
    <ThemeIcon {...props}>
      <AssessmentOutlined
        sx={{
          fontSize: props?.sx?.fontSize || defaultConfig.fontSize,
          color: 'inherit',
        }}
      />
    </ThemeIcon>
  );
};

// ==================== AI ICON WITH BUTTON (Interactive) ====================
export const AICustomIcon = ({
  size = "medium",
  onClick,
  disabled = false,
  sx = {},
  showSparks = true,
  showBurst = true,
  showElectricRing = true,
  ...props
}) => {
  const theme = useTheme();

  const sizeMap = {
    xs: 24,
    small: 28,
    medium: 36,
    large: 44,
    xlarge: 52,
  };

  const iconSize = sizeMap[size] || 36;

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        lineHeight: 0,
        ...sx,
      }}
      {...props}
    >
      <AIButton 
        size={size}
        disabled={disabled}
        onClick={onClick}
        showSparks={showSparks}
        showBurst={showBurst}
        showElectricRing={showElectricRing}
        sx={{
          width: iconSize,
          height: iconSize,
          minWidth: iconSize,
          minHeight: iconSize,
          ...sx,
        }}
      />
    </Box>
  );
};

// ==================== AI DISPLAY ICON (Non-interactive) ====================
/**
 * AIDisplayIcon - Pure display icon without any button functionality
 * Uses the same AIEnergyIcon as the AIButton for consistency
 */
export const AIDisplayIcon = ({
  size = "medium",
  sx = {},
  animated = true,
  color,
  ...props
}) => {
  const theme = useTheme();

  const sizeMap = {
    xs: 16,
    small: 20,
    medium: 28,
    large: 36,
    xlarge: 44,
  };

  const iconSize = sizeMap[size] || 28;

  // Get AI color from theme
  const aiColor = color || (theme.palette.mode === 'dark' 
    ? theme.palette.secondary?.light || '#a78bfa'
    : theme.palette.info?.dark || '#8b5cf6');

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: iconSize,
        height: iconSize,
        color: aiColor,
        animation: animated ? 'ai-icon-float 3s ease-in-out infinite' : 'none',
        cursor: 'default',
        pointerEvents: 'none',
        filter: `drop-shadow(0 0 4px ${aiColor}40)`,
        ...sx,
      }}
      {...props}
    >
      <AIEnergyIcon 
        size={size}
        color="inherit"
        animate={animated}
        isHovered={false}
      />
    </Box>
  );
};

// ==================== AI ICON ALIASES ====================
export const AiIcon = AICustomIcon;
export const AIIcon = AICustomIcon;
export const AIAssistantIcon = AICustomIcon;
export const AssistantIcon = AICustomIcon;
export const SmartAIcon = AICustomIcon;
export const FloxpadAIcon = AICustomIcon;
export const FloxpadAIIcon = AICustomIcon;

// Display icon aliases
export const AiDisplayIcon = AIDisplayIcon;
export const AIDisplay = AIDisplayIcon;
export const FloxpadAIDisplayIcon = AIDisplayIcon;

// ==================== AI ICON WITH LABEL ====================
export const AICustomIconWithLabel = ({
  label = "AI",
  size = "medium",
  onClick,
  disabled = false,
  sx = {},
  labelSx = {},
  ...props
}) => {
  const theme = useTheme();
  
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        cursor: onClick ? 'pointer' : 'default',
        ...sx,
      }}
      onClick={onClick}
    >
      <AICustomIcon 
        size={size} 
        disabled={disabled}
        showSparks={true}
        showBurst={true}
        showElectricRing={true}
        {...props}
      />
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          color: theme.palette.ai?.main || '#8b5cf6',
          fontSize: size === 'small' ? '0.6rem' : '0.7rem',
          ...labelSx,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

// ==================== AI DISPLAY ICON WITH LABEL ====================
export const AIDisplayIconWithLabel = ({
  label = "AI",
  size = "medium",
  sx = {},
  labelSx = {},
  animated = true,
  ...props
}) => {
  const theme = useTheme();
  
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        cursor: 'default',
        ...sx,
      }}
    >
      <AIDisplayIcon 
        size={size}
        animated={animated}
        {...props}
      />
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          color: theme.palette.ai?.main || '#8b5cf6',
          fontSize: size === 'small' ? '0.6rem' : '0.7rem',
          ...labelSx,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

// Add CSS animation for AI icon
const styles = `
@keyframes ai-icon-float {
  0%, 100% {
    transform: translateY(0px);
    filter: drop-shadow(0 0 2px currentColor);
  }
  50% {
    transform: translateY(-4px);
    filter: drop-shadow(0 0 8px currentColor);
  }
}

@keyframes ai-icon-pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.05);
    opacity: 1;
  }
}
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.innerText = styles;
  document.head.appendChild(styleSheet);
}

export { default as ElectricEffects } from "./ElectricEffects";
export { default as AIButton } from "./AIButton";
export { default as AIEnergyIcon } from "./AIEnergyIcon";